import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';

import {
    ContextActionType,
    CustomAvatar,
    EmptyBox,
    FlexySelect,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    OptionsType,
    SkeletonLoader,
    useAvatarUrl,
    useDumbActionsContext,
    useModal,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    antennesSelector,
    groupesSelector,
    marquesSelector,
    polesSelector,
    servicesSelector,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateurApiObject,
    UtilisateursAppDispatch,
    utilisateursSelector,
} from '@europrocurement/l2d-domain';
import {
    UtilisateursApiApiUtilisateursIdsyncPatchRequest,
    UtilisateursJsonldUtilisateurWrite,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import {
    errorNotification,
    successNotification,
} from '@europrocurement/flexy-components/src/components/atoms/NotificationToasters/notificationToasters';
import { FlexyForm } from '@europrocurement/flexy-form';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { FaOptionIcon, plusIcon } from '@europrocurement/l2d-icons';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { customizerSelector, useAppSelector } from '../../../../redux';
import UtilisateurActionMenu from '../../components/molecule/UtilisateurActionMenu';
import FullNameCompiler from '../../components/atom/FullNameCompiler';
import RightsChips from '../../components/molecule/RightsChips';
import {
    createUserFormStructure,
    userDefaultValues,
} from '../../functions/createUserFormStructure';
import { User } from '../../types/type';
import { getSelectsOptions } from '../../functions/getSelectsOptions';
import { KeycloakUserItem } from '../../types/UtilisateursTypes';

const filters: FiltersDatatableList = [
    { label: 'Login', field: 'user', type: 'text' },
    { label: 'Mail', field: 'mail', type: 'text' },
    { label: 'Supprimé', field: 'isDeleted', type: 'boolean' },
    { label: 'Formaliste', field: 'isFormalist', type: 'boolean' },
    { label: 'G3', field: 'hasG3Access', type: 'boolean' },
    { label: 'Cegid', field: 'hasComptaSoftware', type: 'boolean' },
    { label: 'L2DF', field: 'hasAccessL2DF', type: 'boolean' },
    { label: 'Admin Apps', field: 'formalistAdmin', type: 'boolean' },
];

export type PostFormType = {
    nom: string;
    prenom: string;
    initiales: string;
    pole: { label: string; value: number };
    service: { label: string; value: number };
    marque: null;
    idGroupeGie: { label: string; value: number };
    antenne: { label: string; value: number };
    idSociete: { label: string; value: number };
    idSocieteEmployeur: { label: string; value: number };
    formalistAdmin: number;
    formalistDroitLectureDossier: { label: string; value: number };
    formalistDroitLectureGestion: { label: string; value: number };
    formalistDroitSupprDossier: { label: string; value: number };
    formalistDroitModifDossier: { label: string; value: number };
    formalistDroitModifGestion: { label: string; value: number };
    usdAppsAnnuaire: boolean;
    usdAppsAnnuaireOption: boolean;
    usdAppsJalgest: boolean;
    usdAppsJalgestOption: string | null;
    usdAppsMdc: boolean;
    usdAppsMdcOption: string | null;
    usdAppsMdcSpel: boolean;
    usdAppsMdcSpelOption: string | null;
    usdAppsSupport: boolean;
    usdAppsSupportOption: boolean;
    usdAppsXy: boolean;
    usdAppsXyOption: string | null;
    idAntenne: number;
    idUserG3: string;
    idUserDematfacile: string;
} & User;

/**
 * Page de listing des utilisateurs.
 */
const UtilisateursList: React.FunctionComponent = function () {
    const { getUtilisateurs, utilisateurApi, userKeycloakApi } = useUtilisateurService();
    const navigate = useNavigate();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();
    const { usersAvatarInfos } = useAvatarUrl();

    const { xIdSociete } = useAppSelector(customizerSelector);

    const dataSource = useUtilisateursServiceSelector(utilisateursSelector);
    const { isInitialLoading, isInError } = useInitialLoadingState(dataSource);

    const services = useUtilisateursServiceSelector(servicesSelector).data;
    const poles = useUtilisateursServiceSelector(polesSelector).data;
    const marques = useUtilisateursServiceSelector(marquesSelector).data;
    const groupes = useUtilisateursServiceSelector(groupesSelector).data;
    const antennes = useUtilisateursServiceSelector(antennesSelector).data;

    const { servicesOptions, polesOptions, groupesOptions, antennesOptions } = getSelectsOptions({
        services,
        poles,
        marques,
        groupes,
        antennes,
    });

    const columns: ColumnDatatable<UtilisateurApiObject>[] = useMemo(
        () => [
            {
                label: 'ID',
                render: 'id',
                isDisplayed: true,
                sortable: true,
                field: 'id',
                sx: { width: '50px' },
            },
            {
                label: 'Nom',
                isDisplayed: true,
                sortable: true,
                field: 'nom',
                render: (user: UtilisateurApiObject) => (
                    <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                        <CustomAvatar
                            firstName={user.prenom}
                            lastName={user.nom}
                            src={
                                usersAvatarInfos?.find(
                                    (element) =>
                                        element.avatarUserIdentifier === user.userIdentifier,
                                )?.currentUserAvatarUrl
                            }
                            keycloakId={user.userIdentifier}
                            sx={{ height: '32px', width: '32px' }}
                        />
                        <FullNameCompiler user={user} />
                    </Box>
                ),
            },
            {
                label: 'Login',
                render: 'user',
                isDisplayed: true,
                sortable: true,
                field: 'user',
            },
            {
                label: 'Mail',
                render: 'mail',
                isDisplayed: true,
                sortable: true,
                field: 'mail',
            },
            {
                label: 'Société employeur',
                render: (user: UtilisateurApiObject) =>
                    user.libelleSocieteEmployeur ?? 'Non renseigné',
                isDisplayed: true,
                sortable: false,
                field: 'idSocieteEmployeur',
            },
            {
                label: 'Droits',
                render: (user: UtilisateurApiObject) => <RightsChips user={user} />,
                isDisplayed: true,
            },
            {
                label: 'Actions',
                render: (user: UtilisateurApiObject) =>
                    user.id && <UtilisateurActionMenu itemId={user.id} />,
                isDisplayed: true,
                displayLabel: false,
                rowReverse: true,
            },
        ],
        [usersAvatarInfos],
    );

    const [keycloakUser, setKeycloakUser] = useState<KeycloakUserItem | null>(null);

    const breadcrumbPath = useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/utilisateurs', path: 'Utilisateurs' },
            'Gestion des utilisateurs',
        ],
        [],
    );

    const [keycloakUsersList, setKeycloakUsersList] = useState<KeycloakUserItem[]>();
    const getKeycloakSynchronizableUsersList = useCallback(() => {
        userKeycloakApi
            .apiKeycloakUtilisateurssynchronizableGetCollection({ itemsPerPage: 100 })
            .then((response) => {
                setKeycloakUsersList(response.data['hydra:member'] as KeycloakUserItem[]);
            })
            .catch((error) => {
                console.error(error);
                errorNotification(
                    'Une erreur est survenue lors de la récupération des utilisateurs Keycloak.',
                );
            });
    }, [userKeycloakApi]);

    const synchroniseWithKCAccount = useCallback(
        (userId: number) => {
            const requestParam: UtilisateursApiApiUtilisateursIdsyncPatchRequest = {
                id: userId.toString(),
                utilisateursUtilisateurSyncUtilisateur: {},
            };

            utilisateurApi
                .apiUtilisateursIdsyncPatch(requestParam)
                .then(() => {
                    console.debug('Synchronisation Keycloak effectuée.');
                    getKeycloakSynchronizableUsersList();
                })
                .catch((error) => {
                    console.error(error);
                    errorNotification(
                        'Une erreur est survenue lors de la synchronisation avec Keycloak.',
                    );
                });
        },
        [getKeycloakSynchronizableUsersList, utilisateurApi],
    );

    const handleNewUserSubmit = useCallback(
        (formData: PostFormType) => {
            const generatedInitiales = (
                formData.prenom[0] +
                formData.prenom[1] +
                formData.nom[0] +
                formData.nom[1]
            ).toUpperCase();
            // create an OptionType array with the keycloak user list

            let idSocieteData: UtilisateursJsonldUtilisateurWrite['idSociete'] =
                formData.idSociete?.value;
            let idSocieteEmployeurData: UtilisateursJsonldUtilisateurWrite['idSocieteEmployeur'] =
                formData.idSocieteEmployeur?.value;
            let antennaData: UtilisateursJsonldUtilisateurWrite['antenne'] = null;

            if (formData.idSociete !== undefined && formData.idSociete !== null) {
                idSocieteData = formData.idSociete.value;
            }

            if (formData.idSocieteEmployeur !== undefined && formData.idSocieteEmployeur !== null) {
                idSocieteEmployeurData = formData.idSocieteEmployeur.value;
            }

            if (
                formData.antenne !== undefined &&
                formData.antenne !== null &&
                formData.antenne.value !== 0
            ) {
                antennaData = `/utilisateur/antennes/${formData.antenne.value}`;
            }

            const formatedPostData: UtilisateursJsonldUtilisateurWrite = {
                ...formData,
                pole: `/utilisateur/poles/${formData.pole.value}`,
                service: `/utilisateur/services/${formData.service.value}`,
                idSociete: idSocieteData,
                idSocieteEmployeur: idSocieteEmployeurData,
                antenne: antennaData,
                idGroupeGie: `/utilisateur/groupes/${formData.idGroupeGie.value}`,
                initiales: generatedInitiales,
            };

            utilisateurApi
                .apiUtilisateursPost({
                    utilisateursJsonldUtilisateurWrite: formatedPostData,
                })
                .then((response) => {
                    successNotification("L'utilisateur a bien été créé.");
                    dispatch(getUtilisateurs({}));
                    synchroniseWithKCAccount(response.data.id as number);
                    modalActions.close();
                })
                .catch((error) => {
                    console.error(error);
                    if (error.response?.data?.status === 422) {
                        errorNotification('Cet email existe déjà');
                    } else {
                        errorNotification();
                    }
                });
        },
        [dispatch, getUtilisateurs, modalActions, synchroniseWithKCAccount, utilisateurApi],
    );

    /**
     * Creates a new user
     */
    const createUser = useCallback(() => {
        const userCreationValues = {
            ...userDefaultValues,
            mail: keycloakUser?.email,
            user: keycloakUser?.username,
            nom: keycloakUser?.firstName,
            prenom: keycloakUser?.lastName,
            userIdentifier: keycloakUser?.username,
        };

        modalActions.call(
            <FlexyForm
                formObject={userCreationValues}
                formStructure={createUserFormStructure(
                    servicesOptions,
                    polesOptions,
                    groupesOptions,
                    antennesOptions,
                )}
                onSubmit={handleNewUserSubmit}
                gridProps={{ maxWidth: 'md', rowSpacing: 0, columnSpacing: 2 }}
            />,
        );
    }, [
        antennesOptions,
        groupesOptions,
        handleNewUserSubmit,
        keycloakUser,
        modalActions,
        polesOptions,
        servicesOptions,
    ]);

    const keycloakUsersOptions: OptionsType = useMemo(() => {
        if (!keycloakUsersList) return [];
        return keycloakUsersList.map((user) => ({
            label: `${user.lastName} ${user.firstName}`,
            value: user,
        }));
    }, [keycloakUsersList]);

    /**
     * Get the keycloak users list and seed the user form creation with the selected keycloak user
     */
    const handleCreateUser = useCallback(() => {
        modalActions.call(
            <Box
                display="flex"
                flexDirection="column"
            >
                <Typography
                    variant="l2dlabel"
                    sx={{ marginBottom: '5px' }}
                >
                    Sélectionner l&apos;utilisateur Keycloak à créer dans GIE
                </Typography>
                <FlexySelect
                    options={keycloakUsersOptions}
                    onChange={(newValue: { label: string; value: KeycloakUserItem }) => {
                        setKeycloakUser(newValue.value);
                    }}
                    placeholder="Sélectionner..."
                />
            </Box>,
        );
    }, [keycloakUsersOptions, modalActions]);

    // Effet pour ouvrir le formulaire de création après sélection d'un utilisateur Keycloak
    useEffect(() => {
        if (keycloakUser) {
            createUser();
            setKeycloakUser(null);
        }
    }, [keycloakUser, createUser]);

    // Chargement initial des données
    useEffect(() => {
        dispatch(getUtilisateurs({}));
        getKeycloakSynchronizableUsersList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowClick = useCallback(
        (_: React.MouseEvent<HTMLElement>, user: UtilisateurApiObject) => {
            navigate(`${user.id}`);
        },
        [navigate],
    );

    const contextActions: ContextActionType[] = useMemo(
        () => [
            {
                name: 'Nouvel utilisateur',
                icon: <FaOptionIcon {...plusIcon.props} />,
                action: handleCreateUser,
            },
        ],
        [handleCreateUser],
    );

    useDumbActionsContext(contextActions);

    const errorContent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des utilisateurs."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={7}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Nouvel utilisateur"
                        icon={plusIcon}
                        onClick={handleCreateUser}
                        actionType="modal"
                    />
                }
            />

            {dataSource && (
                <StoreDatatable
                    dataSource={dataSource}
                    columns={columns}
                    fetchData={getUtilisateurs}
                    filters={filters}
                    showSearch
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={handleRowClick}
                    observers={[xIdSociete]}
                />
            )}
        </SkeletonLoader>
    );
};

export default UtilisateursList;
